import React, { useState, useEffect } from 'react';
import { Eye, Download, CheckCircle, XCircle, Search, Filter, FileText, Clock, Printer, Send } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate, downloadPDF, sendEmail } from '../../lib/utils';
import { quoteService } from '../../lib/database/quotes';
import { useAuth } from '../../hooks/useAuth';
import { ViewQuoteModal } from './ViewQuoteModal';
import { PrintableQuote } from '../print/PrintableQuote';

export const ClientQuotes: React.FC = () => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [quoteToPrint, setQuoteToPrint] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<{[key: string]: boolean}>({});

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const { data } = await quoteService.getQuotes();
      if (data) {
        // Filter quotes for current client
        const clientQuotes = data.filter(quote => 
          quote.client?.email === user?.email
        );
        setQuotes(clientQuotes);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchQuotes();
    }
  }, [user?.email]);

  const handleViewQuote = (quote: any) => {
    setSelectedQuote(quote);
    setShowViewModal(true);
  };

  const handlePrintQuote = (quote: any) => {
    setQuoteToPrint(quote);
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadPDF = async (quote: any) => {
    setActionLoading({...actionLoading, [`download_${quote.id}`]: true});
    try {
      // In a real app, this would generate a PDF using a library
      await downloadPDF(`Quote_${quote.quote_number}`, JSON.stringify(quote));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`download_${quote.id}`]: false});
    }
  };

  const handleRequestQuoteCopy = async (quote: any) => {
    setActionLoading({...actionLoading, [`email_${quote.id}`]: true});
    try {
      // In a real app, this would send an email with the quote
      await sendEmail(
        quote.client?.email || 'client@example.com',
        `Copy of Quote ${quote.quote_number}`,
        `As requested, please find attached a copy of your quote ${quote.quote_number} for ${formatCurrency(Number(quote.total))}.`
      );
      alert('Quote copy has been sent to your email address.');
    } catch (error) {
      console.error('Error sending quote copy:', error);
      alert('Failed to request quote copy. Please try again.');
    } finally {
      setActionLoading({...actionLoading, [`email_${quote.id}`]: false});
    }
  };

  const handleApproveQuote = async (quoteId: string) => {
    if (window.confirm('Are you sure you want to approve this quote?')) {
      setActionLoading({...actionLoading, [`approve_${quoteId}`]: true});
      try {
        await quoteService.updateQuote(quoteId, { status: 'approved' });
        fetchQuotes(); // Refresh to show updated status
        alert('Quote approved successfully!');
      } catch (error) {
        console.error('Error approving quote:', error);
        alert('Failed to approve quote. Please try again.');
      } finally {
        setActionLoading({...actionLoading, [`approve_${quoteId}`]: false});
      }
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    if (window.confirm('Are you sure you want to reject this quote?')) {
      setActionLoading({...actionLoading, [`reject_${quoteId}`]: true});
      try {
        await quoteService.updateQuote(quoteId, { status: 'rejected' });
        fetchQuotes(); // Refresh to show updated status
        alert('Quote rejected. Thank you for your response.');
      } catch (error) {
        console.error('Error rejecting quote:', error);
        alert('Failed to reject quote. Please try again.');
      } finally {
        setActionLoading({...actionLoading, [`reject_${quoteId}`]: false});
      }
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quote.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || quote.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const pendingQuotes = quotes.filter(q => q.status === 'sent');
  const expiredQuotes = quotes.filter(q => 
    new Date(q.valid_until) < new Date() && q.status !== 'approved'
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white p-6 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Quotes</h1>
            <p className="text-gray-600 mt-2">Review and respond to quotes</p>
          </div>
          {pendingQuotes.length > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-warning-600">{pendingQuotes.length}</p>
            </div>
          )}
        </div>

        {/* Pending Quotes Alert */}
        {pendingQuotes.length > 0 && (
          <Card className="bg-warning-50 border-warning-200">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-warning-600" />
              <div>
                <h3 className="font-semibold text-warning-800">Quotes Awaiting Response</h3>
                <p className="text-warning-700">
                  You have {pendingQuotes.length} quote{pendingQuotes.length !== 1 ? 's' : ''} waiting for your approval
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Search and Filters */}
        <Card padding="sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter:</span>
              </div>
              <div className="flex space-x-2">
                {['all', 'draft', 'sent', 'approved', 'rejected'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                      filter === status
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Quotes List */}
        <div className="space-y-4">
          {filteredQuotes.length === 0 ? (
            <Card className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm || filter !== 'all' ? 'No quotes found' : 'No quotes yet'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Your quotes will appear here when they are created'
                }
              </p>
            </Card>
          ) : (
            filteredQuotes.map((quote) => (
              <Card key={quote.id} hover className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-secondary-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{quote.quote_number}</h3>
                      <p className="text-gray-600">Valid until {formatDate(quote.valid_until)}</p>
                      <p className="text-sm text-gray-500">Created {formatDate(quote.created_at)}</p>
                      {new Date(quote.valid_until) < new Date() && quote.status !== 'approved' && (
                        <p className="text-sm text-error-600 font-medium">Expired</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(Number(quote.total))}</p>
                      <StatusBadge status={quote.status} />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Eye}
                        onClick={() => handleViewQuote(quote)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Printer}
                        onClick={() => handlePrintQuote(quote)}
                      >
                        Print
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Download}
                        onClick={() => handleDownloadPDF(quote)}
                        loading={actionLoading[`download_${quote.id}`]}
                        disabled={actionLoading[`download_${quote.id}`]}
                      >
                        PDF
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        icon={Send}
                        onClick={() => handleRequestQuoteCopy(quote)}
                        loading={actionLoading[`email_${quote.id}`]}
                        disabled={actionLoading[`email_${quote.id}`]}
                      >
                        Email
                      </Button>
                      {quote.status === 'sent' && new Date(quote.valid_until) >= new Date() && (
                        <>
                          <Button 
                            variant="success" 
                            size="sm" 
                            icon={CheckCircle}
                            onClick={() => handleApproveQuote(quote.id)}
                            loading={actionLoading[`approve_${quote.id}`]}
                            disabled={actionLoading[`approve_${quote.id}`] || actionLoading[`reject_${quote.id}`]}
                          >
                            Approve
                          </Button>
                          <Button 
                            variant="error" 
                            size="sm" 
                            icon={XCircle}
                            onClick={() => handleRejectQuote(quote.id)}
                            loading={actionLoading[`reject_${quote.id}`]}
                            disabled={actionLoading[`approve_${quote.id}`] || actionLoading[`reject_${quote.id}`]}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {quote.notes && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">{quote.notes}</p>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="sm" className="text-center bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <p className="text-2xl font-bold text-purple-700">{quotes.length}</p>
            <p className="text-sm text-purple-600">Total Quotes</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-success-50 to-success-100 border-success-200">
            <p className="text-2xl font-bold text-success-700">
              {quotes.filter(q => q.status === 'approved').length}
            </p>
            <p className="text-sm text-success-600">Approved</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-warning-50 to-warning-100 border-warning-200">
            <p className="text-2xl font-bold text-warning-700">
              {pendingQuotes.length}
            </p>
            <p className="text-sm text-warning-600">Pending</p>
          </Card>
          <Card padding="sm" className="text-center bg-gradient-to-br from-error-50 to-error-100 border-error-200">
            <p className="text-2xl font-bold text-error-700">
              {expiredQuotes.length}
            </p>
            <p className="text-sm text-error-600">Expired</p>
          </Card>
        </div>

        {/* View Quote Modal */}
        <ViewQuoteModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedQuote(null);
            fetchQuotes(); // Refresh after closing modal in case status changed
          }}
          quote={selectedQuote}
        />
      </div>

      {/* Printable Version */}
      <PrintableQuote quote={quoteToPrint} isVisible={showPrintView} />
    </>
  );
};